import {
  aantalBezoeken,
  STATUS_TEKST,
  afkoppelStatus,
  contactmomentenOpDatum,
  totaleBergingM3,
  type Foto,
  type Registratie,
} from '@/types/domain';
import { isHuurwoning } from '@/data/huurwoningen';
import { adresRegel, datumNL, vandaagISO } from '@/utils/format';
import { downloadBlob } from '@/utils/download';
import { maakXlsx, type CelWaarde, type Kolom } from '@/utils/xlsx';
import { maakZip, type ZipBestand } from '@/utils/zip';
import { getFotoService } from './serviceFactory';

/**
 * Export voor de gemeente.
 *
 * Bewust een aparte export naast die voor intern gebruik: hier gaan
 * uitdrukkelijk GEEN persoonsgegevens mee. Naam, telefoon, e-mail en de
 * toelichting bij de bewoner blijven eruit.
 *
 * Het verloop van de contactmomenten gaat wel mee — datum, soort en uitkomst,
 * want dat IS de voortgang. Wat er in het gesprek is gezegd niet. Dat is het
 * enige veld waar een coach vrij over een persoon schrijft, en daar geen
 * uitzondering op maken is makkelijker vol te houden dan "meestal niet,
 * behalve bij het laatste contact". De gemeente stuurt op de uitkomst
 * ("Gesproken met bewoner"), het besluit en de vervolgafspraak, en die gaan
 * allemaal wel mee.
 *
 * De gemeente stuurt op adressen, adviezen, kubieke meters en de voortgang.
 * De contactgegevens zijn er alleen zodat de coach kan terugbellen; die horen
 * niet mee te reizen. Door dat hier in de kolomdefinitie te regelen kan het
 * ook niet per ongeluk goed gaan.
 */

const KOLOMMEN: Kolom[] = [
  { kop: 'Adres', breedte: 28 },
  { kop: 'Postcode', breedte: 11 },
  { kop: 'Plaats', breedte: 18 },
  { kop: 'BAG-id', breedte: 20 },
  { kop: 'Koop / huur', breedte: 12 },
  { kop: 'Latitude', breedte: 12 },
  { kop: 'Longitude', breedte: 12 },
  { kop: 'Datum laatste contact', breedte: 16 },
  { kop: 'Aantal contactmomenten', breedte: 18 },
  { kop: 'Waarvan bezoeken', breedte: 15 },
  { kop: 'Verloop contact', breedte: 50 },
  { kop: 'Afkoppelcoach', breedte: 20 },
  { kop: 'Uitkomst bezoek', breedte: 24 },
  { kop: 'Voortuin aanwezig', breedte: 16 },
  { kop: 'Advies voortuin', breedte: 40 },
  { kop: 'Berging voortuin (m3)', breedte: 19 },
  { kop: 'Toelichting voortuin', breedte: 30 },
  { kop: 'Achtertuin aanwezig', breedte: 17 },
  { kop: 'Advies achtertuin', breedte: 40 },
  { kop: 'Berging achtertuin (m3)', breedte: 20 },
  { kop: 'Toelichting achtertuin', breedte: 30 },
  { kop: 'Totale berging (m3)', breedte: 18 },
  { kop: 'Afkoppelen pand', breedte: 22 },
  { kop: 'Beslist over het pand', breedte: 20 },
  { kop: 'Besluit eigenaar over het pand', breedte: 26 },
  { kop: 'Extra maatregelen op het perceel', breedte: 28 },
  { kop: 'Status subsidieaanvraag', breedte: 22 },
  { kop: 'Status realisatie', breedte: 18 },
  { kop: 'Datum vervolgafspraak', breedte: 18 },
  { kop: 'Algemene toelichting', breedte: 40 },
  { kop: 'Aantal fotos', breedte: 12 },
  { kop: 'Laatst gewijzigd', breedte: 18 },
];

function naarRij(registratie: Registratie): CelWaarde[] {
  const { voortuin, achtertuin, adres } = registratie;

  return [
    adresRegel(adres),
    adres.postcode,
    adres.plaats,
    registratie.bagId,
    registratie.eigendom,
    registratie.latitude,
    registratie.longitude,
    datumNL(registratie.datumBezoek),
    registratie.contactmomenten.length,
    aantalBezoeken(registratie),
    // Datum, soort en uitkomst per moment; de toelichting per moment blijft
    // eruit, net als in de gemeenteweergave van de database. Zo laat deze
    // export precies zien wat iemand met de gemeenterol zelf ook zou zien.
    contactmomentenOpDatum(registratie.contactmomenten)
      .map((moment) =>
        [datumNL(moment.datum), moment.soort, moment.uitkomst].filter(Boolean).join(' — ')
      )
      .join(' | '),
    registratie.coach,
    registratie.uitkomstBezoek,
    voortuin.aanwezig,
    voortuin.advies.join(', '),
    voortuin.bergingM3,
    voortuin.toelichting,
    achtertuin.aanwezig,
    achtertuin.advies.join(', '),
    achtertuin.bergingM3,
    achtertuin.toelichting,
    totaleBergingM3(registratie) || null,
    STATUS_TEKST.afkoppelen[afkoppelStatus(registratie, isHuurwoning(registratie.adres))],
    isHuurwoning(registratie.adres) ? 'Corporatie' : 'Eigenaar',
    isHuurwoning(registratie.adres) ? '' : registratie.afkoppelbesluit,
    registratie.maatregelenbesluit,
    registratie.subsidieStatus,
    registratie.realisatieStatus,
    datumNL(registratie.datumVervolg),
    registratie.algemeneToelichting,
    registratie.fotos.length || null,
    datumNL(registratie.gewijzigdOp),
  ];
}

function bestandsnaam(extensie: string): string {
  return `Afkoppelcoach_gemeente_${vandaagISO()}.${extensie}`;
}

/* ------------------------------------------------------------------ */
/* Excel                                                               */
/* ------------------------------------------------------------------ */

export function exporteerGemeenteExcel(registraties: Registratie[]): void {
  const blob = maakXlsx(KOLOMMEN, registraties.map(naarRij), 'Registraties');
  downloadBlob(blob, bestandsnaam('xlsx'));
}

/* ------------------------------------------------------------------ */
/* CSV                                                                 */
/* ------------------------------------------------------------------ */

export function exporteerGemeenteCsv(registraties: Registratie[]): void {
  const escape = (waarde: CelWaarde): string => {
    if (waarde === null || waarde === undefined) return '';
    const tekst = typeof waarde === 'boolean' ? (waarde ? 'Ja' : 'Nee') : String(waarde);
    return `"${tekst.replace(/"/g, '""')}"`;
  };

  const regels = [
    KOLOMMEN.map((kolom) => escape(kolom.kop)).join(';'),
    ...registraties.map((registratie) => naarRij(registratie).map(escape).join(';')),
  ];

  // BOM zodat Excel de UTF-8 tekens goed leest.
  const blob = new Blob([`﻿${regels.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, bestandsnaam('csv'));
}

/* ------------------------------------------------------------------ */
/* GeoJSON                                                             */
/* ------------------------------------------------------------------ */

/**
 * GeoJSON in plaats van GeoPackage: een GeoPackage is een SQLite-bestand en
 * zou een complete database-engine in de browser vereisen. QGIS opent GeoJSON
 * rechtstreeks, en kan het in twee klikken naar GeoPackage omzetten.
 */
export function exporteerGemeenteGeoJson(registraties: Registratie[]): void {
  const features = registraties
    // Zonder coordinaten heeft een punt op de kaart geen betekenis.
    .filter((registratie) => registratie.latitude && registratie.longitude)
    .map((registratie) => {
      const waarden = naarRij(registratie);
      const eigenschappen: Record<string, CelWaarde> = {};
      KOLOMMEN.forEach((kolom, i) => {
        eigenschappen[kolom.kop] = waarden[i];
      });

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          // GeoJSON schrijft lengtegraad voor breedtegraad.
          coordinates: [registratie.longitude, registratie.latitude],
        },
        properties: eigenschappen,
      };
    });

  const geojson = {
    type: 'FeatureCollection' as const,
    name: 'Afkoppelcoach',
    // WGS84; QGIS herkent dit automatisch.
    crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
    features,
  };

  const blob = new Blob([JSON.stringify(geojson, null, 1)], {
    type: 'application/geo+json',
  });
  downloadBlob(blob, bestandsnaam('geojson'));
}

/* ------------------------------------------------------------------ */
/* Foto's                                                              */
/* ------------------------------------------------------------------ */

/** Maakt van een adres een mapnaam die op elk besturingssysteem werkt. */
function adresSleutel(registratie: Registratie): string {
  const schoon = `${adresRegel(registratie.adres)}_${registratie.adres.plaats}`
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

  // Een handmatig punt zonder ingevuld adres zou anders een naamloze map
  // opleveren; dan valt het terug op het BAG-nummer of het registratie-id.
  return schoon || `zonder_adres_${registratie.bagId || registratie.id.slice(0, 8)}`;
}

export interface FotoExportResultaat {
  aantal: number;
  mislukt: number;
}

/**
 * Downloadt alle foto's van de meegegeven registraties als één zipbestand.
 *
 * Indeling: één map met daarin een map per adres, en daarbinnen een map per
 * soort foto:
 *
 *   Afkoppelcoach_fotos_2026-08-23/
 *     Vijfhoevenlaan_52_Raamsdonksveer/
 *       Voorkant/
 *       Achterkant/
 *
 * De bestandsnaam bevat het adres ook, zodat een losse foto die iemand uit de
 * map sleept nog steeds thuis te brengen is.
 *
 * Let op: de browser haalt hiervoor elke foto op en houdt ze in het geheugen.
 * Bij grote aantallen is het beter om per wijk of straat te filteren en dan
 * te downloaden; daarom werkt deze functie op de huidige selectie.
 */
export async function exporteerFotosAlsZip(
  registraties: Registratie[],
  bijVoortgang?: (gedaan: number, totaal: number) => void
): Promise<FotoExportResultaat> {
  const dienst = getFotoService();

  const opdrachten: { registratie: Registratie; foto: Foto; volgnummer: number }[] = [];
  for (const registratie of registraties) {
    const perSoort = new Map<string, number>();
    for (const foto of registratie.fotos) {
      const volgnummer = (perSoort.get(foto.soort) ?? 0) + 1;
      perSoort.set(foto.soort, volgnummer);
      opdrachten.push({ registratie, foto, volgnummer });
    }
  }

  // Alles onder één hoofdmap, zodat uitpakken niet de hele Downloads-map vult.
  const hoofdmap = `Afkoppelcoach_fotos_${vandaagISO()}`;
  const bestanden: ZipBestand[] = [];
  let mislukt = 0;

  for (const [index, opdracht] of opdrachten.entries()) {
    const { registratie, foto, volgnummer } = opdracht;
    try {
      const url = await dienst.weergaveUrl(foto);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`status ${response.status}`);

      const bytes = new Uint8Array(await response.arrayBuffer());
      const adresmap = adresSleutel(registratie);
      const naam =
        `${hoofdmap}/${adresmap}/${foto.soort}/` +
        `${adresmap}_${foto.soort.toLowerCase()}_${volgnummer}.jpg`;
      bestanden.push({ naam, inhoud: bytes });
    } catch {
      // Eén onbereikbare foto mag de hele download niet tegenhouden.
      mislukt += 1;
    }
    bijVoortgang?.(index + 1, opdrachten.length);
  }

  if (bestanden.length > 0) {
    downloadBlob(maakZip(bestanden), `${hoofdmap}.zip`);
  }

  return { aantal: bestanden.length, mislukt };
}
